import { Mixins, Component } from 'vue-property-decorator'
import { CollectionsService, PointsOfInterestService, MediasService, DatasetService, UsersService } from '@/@next/services'
import {
    Collection, 
    MediaAudio,
    Maybe,
    MediaVideo,
    MediaImage,
    PointOfInterest,
    DataToPreload,
    ResourceToPreload,
    FileToPreload
} from '@/@types/graphql'
import addProtocol from '@/utils/addProtocol'
import { CACHE_STRATEGY } from '@/constants';
import * as APP from '@/@next/store/app/types'
import { Getter, Mutation, Action } from 'vuex-class'
import gql from 'graphql-tag'
import { SettingsMixin } from '@/@next/mixins'
import {
    DataToPreloadStrategy,
    PreloadConsentStatus,
    PreloadingDataStatus,
    preloadConfigurationWithForceStrategy,
    preloadConfigurationWithReactNativeWebViewStrategy
} from '../store/app';
import languages from '@/graphql/languages';
import { Language } from '@/@types/graphql';
import { cloneDeep } from 'lodash';
import removeArrayDuplicate from '../helpers/removeArrayDuplicate';

type cachableItemTypes = Collection | PointOfInterest | MediaImage | MediaVideo | MediaAudio

@Component
class RessourceServices extends Mixins(
    CollectionsService,
    PointsOfInterestService,
    MediasService,
    DatasetService,
) {}

@Component({ name: 'preload-service' })
export default class Preload extends Mixins(
    RessourceServices,
    UsersService,
    SettingsMixin) {

    urlList: string[] = [];

    @Getter(APP.PRELOAD_CONFIGURATION) preloadConfiguration!: any
    @Mutation(APP.PRELOAD_CONFIGURATION) setPreloadConfiguration!: any

    @Getter(APP.PRELOAD_IS_READY) preloadIsReady!: boolean
    @Mutation(APP.PRELOAD_IS_READY) setPreloadIsReady!: (boolean: boolean) => void

    @Getter(APP.DATA_TO_PRELOAD) dataToPreload!: DataToPreload
    @Mutation(APP.DATA_TO_PRELOAD) setDataToPreload!: any
    
    @Getter(APP.APP_IS_READY) appIsReady!: boolean

    @Getter(APP.SERVICE_WORKER_IS_READY) serviceWorkerIsReady!: boolean

    @Getter(APP.PRELOAD_FILE_STATUS) preloadFileStatus!: string
    @Mutation(APP.PRELOAD_FILE_STATUS) setPreloadFileStatus!: (status: string) => void

    @Getter(APP.PRELOAD_RESSOURCE_STATUS) preloadRessourceStatus!: string
    @Mutation(APP.PRELOAD_RESSOURCE_STATUS) setPreloadRessourceStatus!: (status: string) => void

    @Mutation(APP.PRELOAD_RESSOURCES) updatePreloadRessources!: any
    @Mutation(APP.PRELOAD_FILES) updatePreloadFiles!: any

    @Getter(APP.PRELOADING) preloading!: boolean
    @Getter(APP.SHOW_PRELOAD_MODAL) showPreloadModal!: boolean
    @Mutation(APP.SHOW_PRELOAD_MODAL) setShowPreloadModal!: (show: boolean) => void

    @Getter(APP.CONSENT_PRELOAD) consentPreload!: PreloadConsentStatus 
    @Mutation(APP.CONSENT_PRELOAD) setConsentPreload!: (consent: PreloadConsentStatus) => void

    @Getter(APP.PRELOAD_ENDED) preloadEnded!: boolean

    @Getter(APP.PRELOAD_SUCCESS) preloadSuccess!: boolean
    @Mutation(APP.PRELOAD_SUCCESS) setPreloadSuccess!: any

    @Getter(APP.PRELOAD_ERROR) preloadError!: boolean
    @Mutation(APP.PRELOAD_ERROR) setPreloadError!: any

    @Getter(APP.NEED_TO_RELOAD_APP) needToReloadApp!: boolean
    @Mutation(APP.NEED_TO_RELOAD_APP) setNeedToReloadApp!: any

    @Getter(APP.DATA_TO_PRELOAD_STRATEGY) preloadStrategy!: DataToPreloadStrategy
    @Mutation(APP.DATA_TO_PRELOAD_STRATEGY) setPreloadStrategy!: (preloadStrategy: DataToPreloadStrategy) => void
    
    @Getter(APP.SHOW_PRELOAD_TOP_BAR_BUTTON) showPreloadTopBarButton!: boolean
    @Mutation(APP.SHOW_PRELOAD_TOP_BAR_BUTTON) setShowPreloadTopBarButton!: (show: boolean) => void

    // concatDataToPreload (data: DataToPreload, { resources, files }: DataToPreload): DataToPreload {
    //     // Concatenate Resources
    //     if (!data.resources?.length) data.resources = []
    //     const resourceTypes = new Set(data.resources.map((r: ResourceToPreload) => r.resourceType))
    //     if (resources?.length) {
    //         for (const input of resources) {
    //             if (resourceTypes.has(input.resourceType)) {
    //               const values = data.resources.find(r => r.resourceType === input.resourceType)!
    //               values.ids = [...new Set([...values.ids, ...input.ids])]
    //             } else {
    //               data.resources.push(input)
    //               resourceTypes.add(input.resourceType)
    //             }
    //           }
    //     }
        
    //     // Concatenate Files
    //     if (!data.files?.length) data.files = []
    //     const mediaTypes = new Set(data.files.map((f: FileToPreload) => f.mediaType))
    //     if (files?.length) {
    //         for (const input of files) {
    //         if (mediaTypes.has(input.mediaType)) {
    //             const values = data.files.find(f => f.mediaType === input.mediaType)!
    //             values.urls = [...new Set([...values.urls, ...input.urls])]
    //         } else {
    //             data.files.push(input)
    //             mediaTypes.add(input.mediaType)
    //         }
    //         }
    //     }
    //     // return concatenated Data to preload
    //     return data
    // }
    
    async fetchPreloadData () {
        try {
            const variables = {
                strategy : this.preloadStrategy,
                language: this._currentLanguage,
                languages: this._userLanguages,
                target: this._languageTarget || null
            }
            const query = gql`
                query dataToPreload (
                    $strategy: DataToPreloadStrategy,
                    $language: LanguageCode!,
                    $languages: [LanguageCode!]!,
                    $target: I18nTarget
                ) {
                    dataToPreload (
                        strategy: $strategy,
                        language: $language,
                        languages: $languages,
                        target: $target
                    ) {
                        resources { resourceType id language languages target }
                        files { mediaType url }
                        configuration
                    }
                }
            `
            const { data } = await this.$apollo.query({ query, variables })
            this.setDataToPreload(data?.dataToPreload || null)
            // Store Preload Configuration
            switch (this.preloadStrategy) {
                case DataToPreloadStrategy.disable:
                    console.info('[Info][Preload] Disable Preloading data ')
                    this.setPreloadConfiguration(null)
                    break
                case DataToPreloadStrategy.force:
                        console.info('[Info][Preload] Force Preloading all data ')
                        this.setPreloadConfiguration(preloadConfigurationWithForceStrategy)
                        break
                case DataToPreloadStrategy.reactNativeWebView:
                    console.info('[Info][Preload] React Native Web context, download all content in all languages')
                    this.setPreloadConfiguration(preloadConfigurationWithReactNativeWebViewStrategy)
                    break
                default: 
                    console.info('[Info][Preload] Use Client Default preload configuration')
                    this.setPreloadConfiguration(data?.dataToPreload?.configuration || null)
                    break
            }
        } catch (e) {
            console.error(`Error fetching preload dataset: ${e}`)
            // Disable preloading data
            this.setDataToPreload(null)
            this.setPreloadConfiguration(null)
        }
    }

    get preloadIsEnabled (): boolean {
        return CACHE_STRATEGY === 'sw-and-preload'
    }

    checkUserConsent () {
        if (!this.preloadConfiguration.needConsent) return this.setConsentPreload(PreloadConsentStatus.accepted)
        return this.consentPreload
    }

    acceptPreload () {
        this.setConsentPreload(PreloadConsentStatus.accepted)
        this.SAVE_CURRENT_USER_PREFERENCES()
    }

    rejectPreload () {
        this.setConsentPreload(PreloadConsentStatus.rejected)
        this.SAVE_CURRENT_USER_PREFERENCES()
    }

    singleItemQuery ( { id, resourceType, ...i18nArgs }: ResourceToPreload) {
        const { language, languages, target } = i18nArgs
        console.log(`singleItemQuery`, id, resourceType, language, languages, target)
        switch (resourceType) {
            case 'MediaImage': return this.GET_SINGLE_IMAGE(id)
            case 'MediaVideo': return this.GET_SINGLE_VIDEO(id)
            case 'MediaAudio': return this.GET_SINGLE_AUDIO(id)
            case 'Collection': return this.GET_SINGLE_COLLECTION(id, { take : 100 }, language, languages, target)
            case 'PointOfInterest': return this.GET_SINGLE_POI(id, languages, target)
            default: return null
        }
    }

    async fetchResources () {
        try {
            if (!this.resourceConfiguration.enable) return
            if (this.resourceConfiguration.needConsent 
                && this.consentPreload !== PreloadConsentStatus.accepted) return console.info('[info] [Preload] Need consent to preload resources')
            console.info('[info][Preload] fetch resources', this.dataToPreload.resources)
            // End if no resources need to be fetched
            if (!this.dataToPreload.resources) return this.setPreloadRessourceStatus(PreloadingDataStatus.complete)
            // Notify about loading of resources
            this.setPreloadRessourceStatus(PreloadingDataStatus.loading)
            // Fetch all GQL resources 
            const getResourceQuery = (resource: ResourceToPreload)  => this.singleItemQuery(resource)
            const promises = this.dataToPreload.resources.map(getResourceQuery) as any[]
            const res = await Promise.all(promises)
            // Notify about fetching resource result
            if (res.filter((r: any) => !res).length) this.setPreloadRessourceStatus(PreloadingDataStatus.uncomplete)
            else this.setPreloadRessourceStatus(PreloadingDataStatus.complete)
            // Store results
            this.updatePreloadRessources({ queries: res, ressources: this.dataToPreload.resources})
        } catch (err) {
            console.log('Error fetching resources')
            this.setPreloadRessourceStatus(PreloadingDataStatus.error)
            this.updatePreloadRessources({ error: err })
        }
    }

    async downloadFiles () {
        try {
            if (!this.filesConfiguration.enable) return
            if (this.filesConfiguration.needConsent 
                && this.consentPreload !== PreloadConsentStatus.accepted)
                return console.info('[info] [Preload] Need consent to preload files')
            console.info('[info][Preload] Download Files', this.dataToPreload.files)
            // End if no files need to be fetched
            if (!this.dataToPreload.files) return this.setPreloadFileStatus(PreloadingDataStatus.complete)
            // Notify about loading of files
            this.setPreloadFileStatus(PreloadingDataStatus.loading)
            const payload = this.dataToPreload.files.map((file: FileToPreload) => ({ url: addProtocol(file.url) }))
            // for (const file of this.dataToPreload.files) {
            //     payload.push(...file.urls.map((url: string) => { 
            //         return { url: addProtocol(url) }
            //     }))
            // }
            console.info(`[info][Preload] Starting preloading ${payload.length} media files`)
            const deletedFiles = await this.sendMessage({ kind: 'files', action: 'delete', payload })
            console.log('[info][Preload] List of deleted old files  ', deletedFiles)
            const cachedFiles: any = await this.sendMessage({ kind: 'files', action: 'preload', payload })
            console.log('[info][Preload] List of cached files cached', cachedFiles)
            if (cachedFiles.payload.filter((f: any) => !f.isSaved).length) 
                this.setPreloadFileStatus(PreloadingDataStatus.uncomplete)
            else this.setPreloadFileStatus(PreloadingDataStatus.complete)
            this.updatePreloadFiles({ cachedFiles: cachedFiles.payload, payload })
        } catch (err) {
            console.log('Error fetching Files')
            this.setPreloadFileStatus(PreloadingDataStatus.error)
            this.updatePreloadFiles({ error: err })
        }
    }

    get resourceConfiguration () {
        return this.preloadConfiguration?.resources || { enable: false }
    }

    get filesConfiguration () {
        return this.preloadConfiguration?.files || { enable: false }
    }

    get needConsent () {
        if (this.preloadStrategy === DataToPreloadStrategy.force) return false
        return (this.resourceConfiguration.enable && this.resourceConfiguration.needConsent)
            || (this.filesConfiguration.enable && this.filesConfiguration.needConsent)
    }

    async downloadOptionalWiviAssets () {
        try {
            // Download flags 
            const flags = this._allLanguages.map((l: Language) => fetch(`/img/icon/flag/4x3/${l.flag}.svg`, { headers: { 'Wivi-Cache': 'save' } }))
            const res = await Promise.all(flags)
            console.info(`[info][Preload] ${res.length} Flags Downloaded`)
        } catch (e) {
            console.warn(`Error during downloading Option Wivi Assets`, e)
        }
        
    }

    async startPreloadingData () {
        try {
            if (!this.dataToPreload) return
            console.info('[info][Preload] starting preloading data', this.dataToPreload)
            await this.fetchResources()
            await this.downloadFiles()
            await this.downloadOptionalWiviAssets()
        } catch (error) {
            console.error('[info][Preload] Error during preloading', error)
            this.setPreloadError(true)
        }
        
    }

    async checkCachedFiles (urls: string[]) {
        const res = await this.sendMessage({
            kind: 'files',
            action: 'check',
            payload: urls.map((url) => {
                return { url: addProtocol(url) }
            })
        })
        return res
    }

    async deleteAllCachedFilesOtherThanList (urls: string[]) {
        const res = await this.sendMessage({
            kind: 'files',
            action: 'delete',
            payload: urls.map((url) => {
                return { url: addProtocol(url) }
            })
        })
        return res
    }

    sendMessage (message: any) {
        // This wraps the message posting/response in a promise, which will resolve if the response doesn't
        // contain an error, and reject with the error if it does. If you'd prefer, it's possible to call
        // controller.postMessage() and set up the onmessage handler independently of a promise, but this is
        // a convenient wrapper.
        return new Promise((resolve, reject) => {
          const messageChannel = new MessageChannel();
          messageChannel.port1.onmessage = (event) => {
            if (event.data.error) {
              reject(event.data.error);
            } else {
              resolve(event.data);
            }
          };
  
          // This sends the message data as well as transferring messageChannel.port2 to the service worker.
          // The service worker can then use the transferred port to reply via postMessage(), which
          // will in turn trigger the onmessage handler on messageChannel.port1.
          // See https://html.spec.whatwg.org/multipage/workers.html#dom-worker-postmessage
          if (!navigator?.serviceWorker?.controller) return resolve('no service worker')
          navigator.serviceWorker.controller.postMessage(message,
            [messageChannel.port2]);
        });
      }
}
